/**
 * useLeaderLines — Imperative SVG leader lines connecting labels to aircraft
 *
 * Creates/removes SVG <line> + <linearGradient> elements only when the
 * visible aircraft set changes. Position updates use setAttribute()
 * in the RAF callback — zero React re-renders during camera movement.
 *
 * Returns { svgElement, updatePositions } — render svgElement in the tree,
 * call updatePositions from the onFrame callback.
 */

import { useRef, useEffect, useCallback, useMemo } from 'react'
import type { ScreenPosition } from '@/services/LabelProjectionService'

/**
 * Stores refs to the SVG elements for a single leader line
 */
interface LineEntry {
  gradient: SVGLinearGradientElement
  line: SVGLineElement
}

export function useLeaderLines(visibleIds: Set<string>) {
  const svgRef = useRef<SVGSVGElement>(null)
  const defsRef = useRef<SVGDefsElement>(null)
  const linesGroupRef = useRef<SVGGElement>(null)
  const entriesRef = useRef<Map<string, LineEntry>>(new Map())

  // Reconcile SVG elements when visibleIds changes
  useEffect(() => {
    const defs = defsRef.current
    const group = linesGroupRef.current
    if (!defs || !group) return

    const entries = entriesRef.current

    // Remove entries that are no longer visible
    for (const [id, entry] of entries) {
      if (!visibleIds.has(id)) {
        entry.gradient.remove()
        entry.line.remove()
        entries.delete(id)
      }
    }

    // Add new entries
    for (const id of visibleIds) {
      if (entries.has(id)) continue

      // Create gradient
      const gradient = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient')
      gradient.id = `leader-grad-${id}`
      gradient.setAttribute('gradientUnits', 'userSpaceOnUse')

      const stop1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop')
      stop1.setAttribute('offset', '0%')
      stop1.setAttribute('stop-color', 'currentColor')
      stop1.setAttribute('stop-opacity', '0.45')

      const stop2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop')
      stop2.setAttribute('offset', '85%')
      stop2.setAttribute('stop-color', 'currentColor')
      stop2.setAttribute('stop-opacity', '0.2')

      const stop3 = document.createElementNS('http://www.w3.org/2000/svg', 'stop')
      stop3.setAttribute('offset', '100%')
      stop3.setAttribute('stop-color', 'currentColor')
      stop3.setAttribute('stop-opacity', '0')

      gradient.appendChild(stop1)
      gradient.appendChild(stop2)
      gradient.appendChild(stop3)
      defs.appendChild(gradient)

      // Create line
      const line = document.createElementNS('http://www.w3.org/2000/svg', 'line')
      line.setAttribute('stroke', `url(#leader-grad-${id})`)
      line.setAttribute('stroke-width', '1.5')
      group.appendChild(line)

      entries.set(id, { gradient, line })
    }
  }, [visibleIds])

  /**
   * Imperatively update line positions. Called from the parent's onFrame callback.
   */
  const updatePositions = useCallback(
    (
      labelPositions: Map<string, ScreenPosition>,
      bodyPositions: Map<string, ScreenPosition>
    ) => {
      const entries = entriesRef.current

      for (const [id, entry] of entries) {
        const labelPos = labelPositions.get(id)
        const bodyPos = bodyPositions.get(id)

        if (!labelPos || !labelPos.visible || !bodyPos) {
          entry.line.setAttribute('x1', '-9999')
          entry.line.setAttribute('y1', '-9999')
          entry.line.setAttribute('x2', '-9999')
          entry.line.setAttribute('y2', '-9999')
          continue
        }

        const lx = String(labelPos.x)
        const ly = String(labelPos.y)
        const bx = String(bodyPos.x)
        const by = String(bodyPos.y)

        entry.gradient.setAttribute('x1', lx)
        entry.gradient.setAttribute('y1', ly)
        entry.gradient.setAttribute('x2', bx)
        entry.gradient.setAttribute('y2', by)

        entry.line.setAttribute('x1', lx)
        entry.line.setAttribute('y1', ly)
        entry.line.setAttribute('x2', bx)
        entry.line.setAttribute('y2', by)
      }
    },
    []
  )

  const svgElement = useMemo(
    () => (
      <svg ref={svgRef} className="absolute inset-0 w-full h-full pointer-events-none">
        <defs ref={defsRef} />
        <g ref={linesGroupRef} />
      </svg>
    ),
    []
  )

  return { svgElement, updatePositions }
}
